import { skipToken, useQuery } from '@tanstack/react-query';

import { Workspaces } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { ONE_HOUR } from '~/constants/unitsOfTime';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscription } from '~/swr-hooks/subscriptions/useSubscription';

export const CREDIT_STATS = 'CREDIT_STATS';

export const getCreditStatsKey = (params: { workspaceId?: string }) => {
  return [CREDIT_STATS, params];
};

export const useCreditStats = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const subscription = useSubscription();
  const isCreditBasedPlan = subscription.data?.pricingModel === 'credit';

  const { creditUsageEnabledForLegacyPlans } = useTemporaryFeatureFlags();
  const isCreditUsageEnabled = creditUsageEnabledForLegacyPlans || isCreditBasedPlan;

  return useQuery({
    queryKey: getCreditStatsKey({ workspaceId }),
    queryFn:
      workspaceId && isCreditUsageEnabled
        ? async () => callAirApi(() => Workspaces.Credits.getStats({ workspaceId }))
        : skipToken,
    /**
     * We want to prevent the somewhat expensive stats endpoint from being called too often, so
     * setting the stale time + refetch interval to one hour. In practice, this will mean that
     * storage/enriched storage usage stats will only be updated once per hour, which should be
     * fine seeing as it is metered in GB units. Generative AI usage will trigger `System.Workspace.Credit.consumed`
     * WebSocket events, and the `useHandleWorkspaceCreditsConsumed` hook will invalidate this query to get
     * the latest stats.
     */
    staleTime: ONE_HOUR,
    refetchInterval: ONE_HOUR,
    refetchIntervalInBackground: false,
  });
};
